import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Card from 'components/global/Card';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useLocale from 'hooks/useLocale';

import { openAxios } from 'services/axios';
import { OPEN } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { faBox, faCalendarAlt, faClockOne, faPeopleCarry, faShippingFast } from '@fortawesome/pro-solid-svg-icons';

import InfoLabel from './component/InfoLabel';
import { Container } from './styled/UndeliveredAttempt1.styled';

export const rescheduleDefaultValues = [
	{
		type: 'color',
		value: '#f5f5f5',
		name: 'pageBackgroundColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'navigationBackgroundColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'iconColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'contentTextColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'contentTitleColor'
	},

	{
		type: 'color',
		value: '#FFFFFF',
		name: 'pageHeaderColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'headerTextColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'headerBackgroundColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'formBackgroundColor'
	},
	{
		type: 'color',
		value: '#3d5fff',
		name: 'submitButtonColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'cancelButtonColor'
	},

	{
		type: 'boolean',
		value: true,
		name: 'showOrderNumber'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showIcon'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnBottom'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnTop'
	}
];

/**
 * @author  Burak Ak
 */

const Reschedule = (props) => {
	const {
		deliveryCompany,
		orderDate,
		otoOrderID,
		orderNumber,
		deliveryDate,
		deliverySlotDate,
		deliverySlotFrom,
		deliverySlotTo,
		status = 'returned',
		propJson,
		getData
	} = props;

	const { showError, showSuccess } = useAlert();
	const [availableTimeslots, setAvailableTimeslots] = useState([]);
	const { getLocaleDate } = useLocale();
	const defaultColSpan = {
		md: 6
	};

	const schema = useMemo(
		() =>
			yup.object().shape({
				deliverySlotDate: yup.string().nullable().required(),
				deliverySlotTime: yup.string().nullable().required()
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			deliverySlotDate: null,
			deliverySlotTime: null
		}
	});
	const { handleSubmit, setValue, watch } = formMethods;

	const onSubmit = async (data) => {
		try {
			const response = await openAxios.post(OPEN.SMS.UPDATE_RESCHEDULE_SMS_DETAILS, {
				otoOrderID: props.otoOrderID,
				...data
			});
			if (response.status === 200) {
				showSuccess();
				getData?.();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	useEffect(() => {
		setValue('deliverySlotTime', null);
		const preAvailableTimeslots = props.availableTimeslots?.find((x) => x.day === watch('deliverySlotDate'));

		setAvailableTimeslots(preAvailableTimeslots ? preAvailableTimeslots.times : []);
	}, [watch('deliverySlotDate')]);

	return (
		<FormProvider {...formMethods}>
			<Container>
				<Card
					padding={1}
					background
					border={0}
					shadowed
					rounded
					style={{
						backgroundColor: propJson?.formBackgroundColor
					}}>
					<Typography
						style={{
							background: propJson?.headerBackgroundColor,
							color: propJson?.headerTextColor,
							borderTopLeftRadius: '4px',
							borderTopRightRadius: '4px'
						}}
						variant="p1"
						className="py-3 text-center mb-2"
						fontWeight="700">
						{status === 'returned'
							? 'rescheduleSMSHeading'
							: status === 'canceled'
								? 'rescheduleSMSHeadingCanceled'
								: 'rescheduleSMSHeadingThankYou'}
					</Typography>
					<div className="mx-5 mt-4">
						<InfoLabel
							label="rescheduleSMSInfoText"
							labelFontWeight="500"
							rowMode
							icon={propJson?.showIcon && faCircleInfo}
							iconSize="xxx-large"
							labelColor={propJson?.contentTitleColor}
							valueColor={propJson?.contentTextColor}
							iconColor={propJson?.iconColor}
							className="my-2 pb-5"
						/>
						<div className="row row-cols-1 row-cols-lg-2">
							{propJson?.showOrderNumber && (
								<InfoLabel
									label="orderNumber"
									value={orderNumber}
									labelFontWeight="500"
									rowMode
									icon={propJson?.showIcon && faBox}
									iconSize="x-large"
									labelColor={propJson?.contentTitleColor}
									valueColor={propJson?.contentTextColor}
									iconColor={propJson?.iconColor}
								/>
							)}
							<InfoLabel
								label="orderDate"
								value={orderDate}
								labelFontWeight="500"
								rowMode
								icon={propJson?.showIcon && faCalendarAlt}
								iconSize="x-large"
								labelColor={propJson?.contentTitleColor}
								valueColor={propJson?.contentTextColor}
								iconColor={propJson?.iconColor}
							/>
						</div>
						<div className="row row-cols-1 row-cols-lg-2">
							<InfoLabel
								label="deliveryCompany"
								value={deliveryCompany}
								labelFontWeight="500"
								rowMode
								icon={propJson?.showIcon && faShippingFast}
								iconSize="x-large"
								labelColor={propJson?.contentTitleColor}
								valueColor={propJson?.contentTextColor}
								iconColor={propJson?.iconColor}
							/>
							<InfoLabel
								label="deliveryDate"
								value={deliveryDate}
								labelFontWeight="500"
								rowMode
								icon={propJson?.showIcon && faPeopleCarry}
								iconSize="x-large"
								labelColor={propJson?.contentTitleColor}
								valueColor={propJson?.contentTextColor}
								iconColor={propJson?.iconColor}
							/>
						</div>

						<div className="row row-cols-1 row-cols-lg-2">
							<InfoLabel
								label={status !== 'returned' ? 'currentDeliverySlotDate' : 'previousDeliverySlotDate'}
								value={`${getLocaleDate(deliverySlotDate)}  ${deliverySlotFrom ?? ''} - ${deliverySlotTo ?? ''}`}
								labelFontWeight="500"
								rowMode
								icon={propJson?.showIcon && faClockOne}
								iconSize="x-large"
								labelColor={propJson?.contentTitleColor}
								valueColor={propJson?.contentTextColor}
								iconColor={propJson?.iconColor}
							/>
						</div>

						{status === 'returned' && (
							<div className="row row-cols-1 row-cols-lg-2">
								<FormItem colSpan={defaultColSpan} label="deliverySlotDate" required>
									<EnumSelect
										name={'deliverySlotDate'}
										data={props.availableTimeslots?.map((option) => option.day)}
										value={watch('deliverySlotDate')}
										setValue={(value) =>
											setValue('deliverySlotDate', value, {
												shouldValidate: true,
												shouldDirty: true
											})
										}
									/>
								</FormItem>

								{watch('deliverySlotDate') && (
									<FormItem colSpan={defaultColSpan} label="deliverySlotTime" required>
										<EnumSelect
											name={'deliverySlotTime'}
											data={availableTimeslots}
											value={watch('deliverySlotTime')}
											setValue={(value) =>
												setValue('deliverySlotTime', value, {
													shouldValidate: true,
													shouldDirty: true
												})
											}
										/>
									</FormItem>
								)}
							</div>
						)}
					</div>

					<div className="mx-5">
						{status === 'returned' && (
							<div style={{ display: 'flex', justifyContent: 'center' }} className="py-3 mt-3">
								<Button
									label="cancelOrder"
									variant="outline"
									onClick={() => {
										NiceModal.show(CancelReasonModal, {
											otoOrderID,
											orderNumber,
											getData
										});
									}}
									style={{ background: propJson?.cancelButtonColor }}
									className="mx-5"
								/>

								<Button
									label="submit"
									variant="blue"
									onClick={handleSubmit(onSubmit, (errors) => {
										AppLogger.log(errors);
									})}
									style={{ background: propJson?.submitButtonColor }}
								/>
							</div>
						)}
					</div>
				</Card>
			</Container>
		</FormProvider>
	);
};
Reschedule.defaultProps = {
	pageBgColor: ' #F5F5F5',
	buttonColor: '#000000',
	headerTextColor: '#FFFFFF',
	headerBgColor: '#000000',
	contentTitleColor: '#000000',
	contentTextColor: '#000000',
	iconColor: ' #000000',
	showIcon: true,
	showOrderNumber: true
};

export default Reschedule;

const CancelReasonModal = NiceModal.create(({ otoOrderID, orderNumber, getData }) => {
	const modal = useModal();
	const { showError, showSuccess } = useAlert();

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					cancelReason: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			cancelReason: null
		}
	});

	const { handleSubmit } = formMethods;

	const handleCloseModal = useCallback(() => {
		modal.remove();
	}, []);

	const onSubmit = async (data) => {
		try {
			const response = await openAxios.post(OPEN.SMS.CANCEL_ORDER_RESCHEDULE_SMS_DETAILS, {
				otoOrderID,
				orderNumber,
				cancelReason: data.cancelReason
			});
			if (response.status === 200) {
				showSuccess();
				getData?.();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	return (
		<Modal fullwidth maxWidth="x-small" className="modal-cancelReasonModal">
			<ModalTitle>
				<Typography variant="p1" noWrap>
					cancelReasonModal
				</Typography>
			</ModalTitle>

			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="cancelReason" toolTip="cancelReason.tooltip" required>
						<TextArea name="cancelReason" />
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} className="mr-3" />

				<Button
					label="save"
					variant="blue"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});
